import React, { useEffect, useState } from "react";
import styles from "./Accordion.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import useWindowSize from "../../hooks/useWindowSize";
import { ReactSVG } from "react-svg";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Options_Modul_Akkordeon } from "@/wp/types/acf-module-options-types";
import {
  ExtendedLink,
  ExtendedLinkType,
} from "@/components/ExtededLink/ExtendedLink";
import { StaggerFadeInUp, FadeIn } from "@/components/Animation";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { ImageType } from "@/wp/types/acf-module-types";

interface AccordionItemProps {
  elements: {
    title: string;
    content: string;
    link_extended?: ExtendedLinkType;
    text_image?: {
      content: string;
      image: ImageType | false;
    };
  }[];
  border_line: {
    color: string;
    height: number;
  };
  separator: "box" | "border";
  box_color: string;
  moduleIndex: number;
  showExtendedLink: Options_Modul_Akkordeon["show_extended_link"];
  buttonType?: Options_Modul_Akkordeon["button_type"];
  initialOpenIndex?: number | null;
  hasBackgroundImage?: boolean;
}

export const AccordionItems = ({
  elements,
  border_line,
  separator,
  moduleIndex,
  showExtendedLink = false,
  buttonType,
  initialOpenIndex = null,
  hasBackgroundImage = false,
}: AccordionItemProps) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(
    initialOpenIndex,
  );
  const { width } = useWindowSize();
  const { getIcon } = useGlobalContext();

  const randomId = moduleIndex;

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const { key } = event;
    switch (key) {
      case "Enter":
      case " ":
        event.preventDefault();
        setOpenedIndex(openedIndex === index ? null : index);
        break;
      case "ArrowDown":
        event.preventDefault();
        focusNextHeader(index);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusPreviousHeader(index);
        break;
      case "Home":
        event.preventDefault();
        focusHeader(0);
        break;
      case "End":
        event.preventDefault();
        focusHeader(elements.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        setOpenedIndex(null);
        blurHeader();
        break;
      default:
        break;
    }
  };

  const focusNextHeader = (index: number) => {
    const nextIndex = index + 1 >= elements.length ? 0 : index + 1;
    focusHeader(nextIndex);
  };

  const focusPreviousHeader = (index: number) => {
    const prevIndex = index - 1 < 0 ? elements.length - 1 : index - 1;
    focusHeader(prevIndex);
  };

  const focusHeader = (index: number) => {
    const header = document.getElementById(`summary-${randomId}-${index}`);
    header?.focus();
  };

  const blurHeader = () => {
    const focusedElement = document.activeElement as HTMLElement;
    focusedElement.blur();
  };

  useEffect(() => {
    const details = document.querySelectorAll(
      `.details-${randomId}`,
    ) as NodeListOf<HTMLElement>;

    details.forEach((detail, index) => {
      const summary = detail.querySelector(
        `.summary-${randomId}`,
      ) as HTMLElement;
      const content = detail.querySelector(
        `.content-${randomId}`,
      ) as HTMLElement;

      if (summary) {
        // Calculate the height of the summary
        // Add the summary margin offset unless the gradient border removes margins.
        const summaryMarginOffset = hasBackgroundImage ? 0 : 4;
        const summaryHeight = summary.offsetHeight + summaryMarginOffset;
        const contentHeight = content.offsetHeight;

        // Add the styles to the details tag
        if (openedIndex === index) {
          detail.style.maxHeight = `${summaryHeight + contentHeight}px`;
        } else {
          detail.style.maxHeight = `${summaryHeight}px`;
        }

        detail.style.overflow = "hidden";
        detail.style.transition = "all 0.3s ease-in-out";
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedIndex, width, elements, hasBackgroundImage]);

  return (
    <StaggerFadeInUp
      className={`${styles["accordion-items"]} ${styles[separator]}`}
    >
      {elements.map((element, index) => {
        return (
          // accordion item
          <FadeIn key={index}>
            <details
              className={`${styles["accordion-item"]} details-${randomId}`}
              style={{
                borderBottom:
                  separator === "border" && index < elements.length - 1
                    ? `${border_line.height}px solid var(--accordion-border-line-color)`
                    : "none",
              }}
              open
            >
              <summary
                id={`summary-${randomId}-${index}`}
                className={`${styles["summary"]} summary-${randomId}  ${styles[separator]}`}
                onClick={(e) => {
                  e.preventDefault();
                  setOpenedIndex(openedIndex === index ? null : index);
                }}
                style={{
                  background:
                    separator === "box"
                      ? "var(--accordion-box-color)"
                      : "transparent",
                }}
                onKeyDown={(event) => handleKeyDown(event, index)}
                aria-expanded={openedIndex === index ? "true" : "false"}
                aria-controls={`accordion-content-${randomId}-${index}`}
                aria-pressed={openedIndex === index ? "true" : "false"}
              >
                <div className={`${styles["accordion-item-header"]}`}>
                  <span className={`h3 has-text-left mr-4 ${styles.title}`}>
                    {element.title}
                  </span>
                  <span
                    className={`${styles["icon"]} ${openedIndex === index ? styles["is-open"] : ""}`}
                  >
                    <ReactSVG
                      style={{ fill: "var(--accordion-icons-color)" }}
                      width={20}
                      height={20}
                      src={getIcon("open")}
                      wrapper={"svg"}
                      beforeInjection={(svg) => {
                        svg.setAttribute("alt", `Open Icon`);
                        svg.setAttribute("aria-label", `Open Icon`);
                      }}
                    />
                  </span>
                </div>
              </summary>

              <div
                className={`${styles["accordion-item-content"]} content-${randomId}  ${styles[separator]}`}
                id={`accordion-content-${randomId}-${index}`}
                style={{
                  background:
                    separator === "box"
                      ? "var(--accordion-box-color)"
                      : "transparent",
                }}
              >
                <div
                  id={`accordion-content-text-${randomId}-${index}`}
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(element.content),
                  }}
                />
                {element.text_image?.content && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: customSanitize(element.text_image.content),
                    }}
                  />
                )}
                {element.text_image?.image && (
                  <MediaItem
                    image_desktop={element.text_image.image}
                    sizes="(max-width: 767px) 100vw, 50vw"
                    className={styles["accordion-text-image"]}
                  />
                )}
                {showExtendedLink && buttonType && element.link_extended && (
                  <ExtendedLink
                    extendedLink={element.link_extended}
                    btnType={buttonType}
                  />
                )}
              </div>
            </details>
          </FadeIn>
        );
      })}
    </StaggerFadeInUp>
  );
};
